<template>
  <div class="company-form-wrapper">
    <form
      class="row py-4 company-form"
      @submit.prevent="save"
    >
      <div
        class="col-12"
        :class="{'col-md-8 border-right': !isNew}"
      >
        <div class="form-group">
          <label for="place-of-business">
            Google Search
          </label>
          <maps-auto-complete
            id="place-of-business"
            ref="address"
            :class="{ 'is-invalid': error && error.errors['address.address1'] }"
            :types="null"
            class="form-control"
            @placechanged="setPlace"
            @keydown.enter.prevent.native
          />
        </div>

        <div class="form-group">
          <label>Legal Name</label>
          <multiselect
            v-model="lei"
            :options="leis"
            :internal-search="false"
            :allow-empty="false"
            :searchable="true"
            :multiple="false"
            :show-labels="false"
            class="multiselect"
            style="cursor:pointer"
            select-label="Select"
            label="LegalName"
            track-by="LEI"
            @search-change="delayGetLei"
          />

          <form-control-error :errors="(error && error.errors.parent_account_id) ? error.errors.parent_account_id : []" />
        </div>

        <form-control
          v-model="company.name"
          :errors="error ? error.errors.name : []"
          placeholder="Add company name"
          title="Name"
        />

        <form-control
          v-model="company.short_name"
          :errors="error ? error.errors.short_name : []"
          placeholder="Add company short name"
          title="Short Name"
        />

        <div class="form-group">
          <label>Country</label>
          <multiselect
            v-model="company.country"
            :options="countries"
            :show-labels="false"
            :class="{
              'is-invalid': ((error && error.errors.country_id) ? error.errors.country_id : []).length > 0
            }"
            placeholder="Type to search"
            track-by="id"
            label="name"
            @select="(val) => company.country_id = val.id"
          >
            <span slot="noResult">
              No elements found.
            </span>
            <span
              slot="placeholder"
              class="multiselect__placeholder"
            >
              Type to search
            </span>
          </multiselect>
          <form-control-error
            :errors="(error && error.errors.country_id) ? error.errors.country_id : []"
          />
        </div>

        <div class="form-group">
          <label>Sector</label>
          <multiselect
            v-model="company.micro_sector"
            :options="sectors"
            :show-labels="false"
            :class="{
              'is-invalid': ((error && error.errors.micro_sector_id) ? error.errors.micro_sector_id : []).length > 0
            }"
            group-values="micro_sectors"
            group-label="name"
            placeholder="Type to search"
            track-by="name"
            label="name"
            @select="(val) => company.micro_sector_id = val.id"
          >
            <span slot="noResult">
              No elements found.
            </span>
            <span
              slot="placeholder"
              class="multiselect__placeholder"
            >
              Type to search
            </span>
          </multiselect>
          <form-control-error
            :errors="(error && error.errors.micro_sector_id) ? error.errors.micro_sector_id : []"
          />
        </div>

        <div class="form-group">
          <label>Market Cap</label>
          <multiselect
            v-model="company.market_cap"
            :options="marketCaps"
            :allow-empty="false"
            :show-labels="false"
            :class="{
              'is-invalid': ((error && error.errors[`market_cap_id`]) ? error.errors[`market_cap_id`] : []).length > 0
            }"
            :searchable="false"
            placeholder="Select market cap"
            class="multiselect"
            style="cursor:pointer"
            select-label="Select"
            label="name"
            track-by="id"
            @select="(val) => company.market_cap_id = val.id"
          >
            <template
              v-slot:option="{ option }"
            >
              {{ option.name }} ({{ option.description }})
            </template>
          </multiselect>
          <form-control-error
            :errors="(error && error.errors.market_cap_id) ? error.errors.market_cap_id : []"
          />
        </div>

        <form-control
          v-model="company.ticker"
          :errors="error ? error.errors.ticker : []"
          placeholder="Ticker"
          title="Ticker"
        />

        <form-control
          v-model="company.website"
          :errors="error ? error.errors.website : []"
          placeholder="Add website"
          title="Website"
        />

        <form-control
          v-model="company.lei"
          :errors="error ? error.errors.lei : []"
          placeholder="LEI"
          title="LEI"
          disabled
        />

        <!-- <form-control
          v-model="company.address.phone"
          :errors="error ? error.errors.phone : []"
          :no-margin="true"
          placeholder="Add phone number"
          title="Phone"
        /> -->
      </div>
      <slot />
    </form>

    <confirm
      @confirmed="deleteCompany"
      :name="company.name"
    />

    <!-- TICKER MODAL -->
    <modal
      v-if="company.id"
      :classes="['v--modal', 'vue-dialog']"
      :adaptive="true"
      :scrollable="true"
      height="auto"
      name="add-ticker"
    >
      <div class="py-3 mx-4 d-flex border-bottom modal__header">
        <h2 class="m-0">
          Add Ticker
        </h2>
        <button
          class="close ml-auto"
          @click="$modal.hide('add-ticker')"
        >
          <icon icon="close" />
        </button>
      </div>
      <div class="mt-3 px-4">
        <ticker-form
          ref="ticker"
          :company-id="company.id"
          @created="addTicker"
        />

        <div class="mt-3 py-4 d-flex border-top">
          <button
            class="mr-2 btn btn-outline-primary  ml-auto"
            type="button"
            @click.stop="$modal.hide('add-ticker')"
          >
            Cancel
          </button>
          <button
            class="btn btn-primary ml-1"
            type="button"
            @click.stop="$refs.ticker.add()"
          >
            Save
          </button>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {getOnce, debounce} from '@/helpers'
import TickerForm from '@/components/ticker-form/TickerForm'
import Confirm from '@/components/Confirm'
import MapsAutoComplete from 'vue-google-autocomplete'
import {successNotification} from '@/notifications'
import request from 'request'

export default {
  components: {
    TickerForm,
    MapsAutoComplete,
    Confirm,
  },

  props: {
    error: {
      required: false,
      type: Object,
      default: null,
    },
    value: {
      required: true,
      type: Object,
    },

    params: {
      required: false,
      type: Object,
      default () {
        return {}
      },
    },

    isModal: {
      required: false,
      type: Boolean,
      default: () => false,
    },
  },

  data () {
    return {
      isNew: this.isModal || this.$route.name.includes('create'),
      addressShowRemove: [],
      countries: [],
      sectors: [],
      loading: false,
      marketCaps: [],
      lei: null,
      leis: [],
      company: {
        country_id: null,
        country: null,
        lei: null,
        ...this.value,
      },
    }
  },

  watch: {
    value: {
      deep: true,
      handler () {
        this.company = this.value
        this.reset()
      },
    },

    company: {
      deep: true,
      handler () {
        this.$emit('input', this.company)
      },
    },

    lei: {
      async handler () {
        this.company.lei = this.lei.LEI
        await this.getLeiInfo()
      },
    },
  },

  async mounted () {
    console.log(this.company)
    try {
      const [countries, sectors, marketCaps] = await Promise.all([
        getOnce('countries'),
        getOnce('sectors', {populate: ['microSectors']}),
        getOnce('market-caps'),
      ])
      this.countries = countries.data.data
      this.sectors = sectors.data.data
      this.marketCaps = marketCaps.data.data

      this.reset()
    } catch (error) {
      throw new Error(error)
    }
  },

  methods: {
    delayGetLei: debounce(function (query) {
      if (query.length >= 5) {
        this.$http.get(`https://api.leilex.com/API/LEI/AutoComplete/?filterType=Name&query=${query}`).then((response) => {
          this.leis = response.data
        })
      }
    }, 200),

    reset () {
      if (this.company.country_id) {
        this.company.country = this.countries.find((c) => this.company.country_id === c.id)
      }
      if (this.company.market_cap_id) {
        this.company.market_cap = this.marketCaps.find((c) => this.company.market_cap_id === c.id)
      }
      if (this.company.micro_sector_id) {
        this.sectors.forEach((sector) => {
          sector.micro_sectors.forEach((ms) => {
            if (ms.id === this.company.micro_sector_id) {
              this.company.micro_sector = ms
            }
          })
        })
      }
    },

    async getLeiInfo () {
      request(`https://leilookup.gleif.org/api/v2/leirecords?lei=${this.lei.LEI}`, (err, response, body) => {
        body = JSON.parse(body)
        if (body.length > 0) {
          const entity = body[0].Entity
          console.log(entity)
          // let address2 = ''
          // this.data.address.address1 = entity.LegalAddress.FirstAddressLine['$'] || null
          // for (const line of entity.LegalAddress.AdditionalAddressLine) {
          //   address2 += line['$']
          // }
          // this.data.address.address2 = address2
          // this.data.address.city = entity.LegalAddress.City['$']
          // this.data.address.postal_code = entity.LegalAddress.PostalCode['$']

          const name = entity.LegalName['$']
          const shortName = name.toLowerCase().replace(/(?<= )[^\s]|^./g, (a)=>a.toUpperCase())

          this.company.name = name
          this.company.short_name = shortName
              .replace(/\sLlc$/, '')
              .replace(/\sInc\.?$/, '')
              .replace(/\sLimited\.?$/, '')
              .replace(/\sLtd\.?$/, '')

          const country = this.findCountryBy2Alpha(entity.LegalAddress.Country['$'])
          this.company.country_id = country.id
          this.company.country = country
        }
      })
    },

    preformSave () {
      const data = Object.assign(this.params, this.company)

      return this.company.id
        ? this.$http.put('companies/' + this.company.id, data)
        : this.$http.post('companies', data)
    },

    save () {
      this.$emit('submit')
    },

    deleteCompany () {
      this.$http.delete('companies/' + this.company.id).then(() => {
        this.$notify(successNotification(`Deleted ${this.company.name}`))
        this.$router.replace({name: 'companies'})
      })
    },

    selectedChannel (selected) {
      this.company.channel_id = selected ? selected.object.id : null
    },

    findInPlace (place, component, type = 'long_name') {
      const value = place.address_components.filter((c) => {
        return c.types.indexOf(component) !== -1
      })

      return value.length > 0 ? value[0][type] : null
    },

    findCountryBy2Alpha (alpha2Code) {
      return this.countries.filter((country) => {
        return country.alpha_2_code === alpha2Code
      }).shift()
    },

    setPlace (place, obj) {
      this.delayGetLei(obj.name)
      let vicinity
      if (obj.vicinity) {
        vicinity = obj.vicinity.split(',')
        vicinity.pop()
      }

      const alpha2Code = this.findInPlace(obj, 'country', 'short_name')
      const country = alpha2Code ? this.findCountryBy2Alpha(alpha2Code) : null
      if (!this.company.address) {
        this.company.address = {}
      }
      if (country) {
        this.company.address.country = country
        this.company.address.country_id = country.id
        this.company.country_id = country.id
        this.company.country = country
      }

      this.company.address.address1 = (!place.street_number && !place.route)
        ? vicinity.join(',').trim()
        : `${place.street_number || ''} ${place.route || ''}`

      this.company.address.address2 = ''
      this.company.address.city = this.findInPlace(obj, 'locality')
      this.company.address.state = this.findInPlace(obj, 'administrative_area_level_1', 'short_name')
      this.company.address.postal_code = this.findInPlace(obj, 'postal_code')

      if (!this.company.address.city) {
        this.company.address.city = this.findInPlace(obj, 'postal_town')
      }

      if (obj.international_phone_number) {
        this.company.address.phone = obj.international_phone_number
      } else if (obj.formatted_phone_number) {
        this.company.address.phone = obj.formatted_phone_number
      }

      if (obj.website && !this.company.website) {
        this.company.website = obj.website
      }

      if (obj.name && !this.company.name) {
        this.company.name = obj.name
        this.company.short_name = obj.name
      }
    },

    addTicker (response) {
      this.company.tickers.push(response.data)
      this.$modal.hide('add-ticker')
    },

    deleteTicker (ticker) {
      this.$notify({
        group: 'confirm',
        type: 'error',
        title: `Are you sure you want to delete ${ticker.ticker}?`,
        data: {
          onConfirm: () => {
            this.$http.delete('tickers/' + ticker.id).then(() => {
              this.company.tickers.splice(this.company.tickers.indexOf(ticker), 1)
              this.$notify(successNotification(`Deleted ${ticker.ticker}`))
            })
          },
        },
      })
    },
  },
}
</script>
